import React from "react";
import { FiMessageSquare, FiSettings, FiCheckCircle, FiGlobe, FiSend, FiExternalLink } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";

function GatewaySettings({ openSmsSettings, openWhatsAppSettings, onClose }) {
  return (
    <div className="w-full space-y-4 font-['Inter',sans-serif]">
      {/* Top Banner Information */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 text-lg shrink-0">
            <FiGlobe className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xs sm:text-sm font-bold text-slate-800 tracking-tight uppercase">
              Communication &amp; Messaging Gateways
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Configure third-party transactional SMS providers, WhatsApp Business Cloud APIs &amp; automated notification pipelines.
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold px-2 py-1 rounded-full flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            2 Services Online
          </span>
        </div>
      </div>

      {/* Gateway Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* SMS Gateway Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:border-blue-300 transition flex flex-col justify-between space-y-3">
          <div className="space-y-2.5">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div className="flex items-center space-x-2.5">
                <div className="w-9 h-9 rounded-lg bg-blue-100/70 text-blue-700 flex items-center justify-center text-base shrink-0">
                  <FiMessageSquare className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-slate-900 leading-tight">
                    SMS Gateway Integration
                  </h3>
                  <span className="text-[10px] text-slate-400">Transactional &amp; Promotional SMS</span>
                </div>
              </div>
              <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                <FiCheckCircle className="text-emerald-600 text-[10px]" /> Connected
              </span>
            </div>

            <p className="text-[11px] text-slate-600 leading-relaxed">
              Automate customer notifications, scheme payment confirmations, birthday wishes &amp; OTP verification directly via HTTP SMS Gateways.
            </p>

            <div className="space-y-1 bg-slate-50 p-2.5 rounded-lg border border-slate-100 text-[10px] text-slate-600">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-500">Service Route:</span>
                <span className="font-Inter font-bold text-slate-700">DND / High Priority HTTP</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-500">Auto Trigger:</span>
                <span className="font-medium text-emerald-600">Instant on Scheme Receipt</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-500">Default Sender ID:</span>
                <span className="font-Inter font-bold text-slate-700">SHRGOLD</span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => openSmsSettings && openSmsSettings()}
            className="w-full py-2 bg-slate-50 hover:bg-blue-50 text-blue-700 hover:text-blue-800 font-bold text-xs rounded-lg border border-slate-200 hover:border-blue-300 transition flex items-center justify-center gap-1.5 shadow-2xs active:scale-98 cursor-pointer"
          >
            <FiSettings className="w-3.5 h-3.5" />
            <span>Configure SMS Settings</span>
          </button>
        </div>

        {/* WhatsApp Business API Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:border-emerald-300 transition flex flex-col justify-between space-y-3">
          <div className="space-y-2.5">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div className="flex items-center space-x-2.5">
                <div className="w-9 h-9 rounded-lg bg-emerald-100/70 text-emerald-700 flex items-center justify-center text-base shrink-0">
                  <FaWhatsapp className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-slate-900 leading-tight">
                    WhatsApp Business API
                  </h3>
                  <span className="text-[10px] text-slate-400">Direct Customer Messaging</span>
                </div>
              </div>
              <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                <FiCheckCircle className="text-emerald-600 text-[10px]" /> API Active
              </span>
            </div>

            <p className="text-[11px] text-slate-600 leading-relaxed">
              Dispatches digital PDF receipt copies, installment schedules, catalog updates, and instant payment confirmations to customer WhatsApp numbers.
            </p>

            <div className="space-y-1 bg-slate-50 p-2.5 rounded-lg border border-slate-100 text-[10px] text-slate-600">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-500">Gateways:</span>
                <span className="font-Inter font-bold text-slate-700">Rapiwha &amp; GreenAds Telinfy</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-500">PDF Dispatch:</span>
                <span className="font-medium text-emerald-600">Enabled (Auto-Attach)</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-500">Template Engine:</span>
                <span className="font-Inter font-bold text-slate-700">Meta Multi-Language Dynamic</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-500">Cloud Webhook:</span>
                <span className="font-medium text-emerald-600">Active (Instant Delivery)</span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => openWhatsAppSettings && openWhatsAppSettings()}
            className="w-full py-2 bg-slate-50 hover:bg-emerald-50 text-emerald-700 hover:text-emerald-800 font-bold text-xs rounded-lg border border-slate-200 hover:border-emerald-300 transition flex items-center justify-center gap-1.5 shadow-2xs active:scale-98 cursor-pointer"
          >
            <FiSettings className="w-3.5 h-3.5" />
            <span>Configure WhatsApp Settings</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default GatewaySettings;
